import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { AlertCircle, User, Mail, Phone, Plane } from 'lucide-react';

const PassengerForm = ({ passengerDetails, errors, onChange }) => {
  return (
    <Card className="bg-secondary border-border">
      <CardContent className="p-6">
        <h2 className="font-display text-xl font-bold text-foreground mb-6">
          Passenger Details
        </h2>

        <div className="space-y-5">
          {/* Name Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-foreground font-medium text-sm">
                First Name *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="firstName"
                  name="firstName"
                  placeholder="John"
                  value={passengerDetails.firstName}
                  onChange={onChange}
                  className={`pl-10 h-12 bg-background border text-foreground placeholder:text-muted-foreground/60 ${
                    errors.firstName ? 'border-primary' : 'border-border'
                  }`}
                  autoComplete="given-name"
                />
              </div>
              {errors.firstName && (
                <div className="flex items-center gap-1.5 text-primary text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errors.firstName}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-foreground font-medium text-sm">
                Last Name *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="lastName"
                  name="lastName"
                  placeholder="Smith"
                  value={passengerDetails.lastName}
                  onChange={onChange}
                  className={`pl-10 h-12 bg-background border text-foreground placeholder:text-muted-foreground/60 ${
                    errors.lastName ? 'border-primary' : 'border-border'
                  }`}
                  autoComplete="family-name"
                />
              </div>
              {errors.lastName && (
                <div className="flex items-center gap-1.5 text-primary text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errors.lastName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground font-medium text-sm">
              Email Address *
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john@example.com"
                value={passengerDetails.email}
                onChange={onChange}
                className={`pl-10 h-12 bg-background border text-foreground placeholder:text-muted-foreground/60 ${
                  errors.email ? 'border-primary' : 'border-border'
                }`}
                autoComplete="email"
              />
            </div>
            {errors.email && (
              <div className="flex items-center gap-1.5 text-primary text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-foreground font-medium text-sm">
              Phone Number *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+44 123 456 7890"
                value={passengerDetails.phone}
                onChange={onChange}
                className={`pl-10 h-12 bg-background border text-foreground placeholder:text-muted-foreground/60 ${
                  errors.phone ? 'border-primary' : 'border-border'
                }`}
                autoComplete="tel"
              />
            </div>
            {errors.phone && (
              <div className="flex items-center gap-1.5 text-primary text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{errors.phone}</span>
              </div>
            )}
          </div>

          {/* Flight Number (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="flightNumber" className="text-foreground font-medium text-sm">
              Flight Number <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <div className="relative">
              <Plane className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="flightNumber"
                name="flightNumber"
                placeholder="TK1234"
                value={passengerDetails.flightNumber}
                onChange={onChange}
                className="pl-10 h-12 bg-background border-border text-foreground placeholder:text-muted-foreground/60"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              We'll track your flight to adjust pickup time if needed
            </p>
          </div>

          {/* Special Requests (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="specialRequests" className="text-foreground font-medium text-sm">
              Special Requests <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="specialRequests"
              name="specialRequests"
              placeholder="Child seat, wheelchair accessible, etc."
              value={passengerDetails.specialRequests}
              onChange={onChange}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground/60 min-h-[80px]"
            />
          </div>

          {/* Terms & Privacy Agreement */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="termsAccepted"
                name="termsAccepted"
                checked={passengerDetails.termsAccepted || false}
                onCheckedChange={(checked) => {
                  onChange({ target: { name: 'termsAccepted', value: checked, type: 'checkbox' } });
                }}
                className="mt-1"
                data-testid="terms-checkbox"
              />
              <div className="space-y-1">
                <Label 
                  htmlFor="termsAccepted" 
                  className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
                >
                  I agree to the{' '}
                  <Link 
                    to="/terms-of-service/" 
                    target="_blank" 
                    className="text-primary hover:underline"
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link 
                    to="/privacy-policy/" 
                    target="_blank" 
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  . I understand the{' '}
                  <Link 
                    to="/cancellation-refund-policy/" 
                    target="_blank" 
                    className="text-primary hover:underline"
                  >
                    Cancellation & Refund Policy
                  </Link>
                  . *
                </Label>
              </div>
            </div>
            {errors.termsAccepted && (
              <div className="flex items-center gap-1.5 text-primary text-sm mt-2">
                <AlertCircle className="h-4 w-4" />
                <span>{errors.termsAccepted}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PassengerForm;
